import { useContext } from 'react'

import { faFlagSwallowtail } from '@fortawesome/pro-light-svg-icons/faFlagSwallowtail'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Button from '@space-core/components/button'
import type { Variation } from '@space-core/components/button'
import LineClamp from '@space-core/components/lineClamp'
import useTranslation from '@space-core/hooks/useTranslation'
import { LearningTrackCard as Card } from '@space-shell/common/components/card/learningTrack'
import ContentLoader from '@space-shell/common/components/contentLoader'
import LockIcon from '@space-shell/common/components/lockIcon'
import SectionTypeTag from '@space-shell/common/components/sectionTypeTag'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import type { Track, SectionProductDetail } from '@space-shell/common/services/sections'
import { useMembershipStore } from '@space-shell/core/store'
import useGetProductDetails from '@space-shell/modules/home/hooks/useGetProductDetails/useGetProductDetails'
import useProductClick from '@space-shell/modules/home/hooks/useProductClick'
import { useSalesPage } from '@space-shell/modules/sales/presentation/hooks/common/use-sales-page'
import styled from 'styled-components'

import { getLearningTrackLink, learningTrackCallToActions } from './utils'

import useProduct from '../../../products/context/hook'

const ButtonsContainer = styled.div`
  gap: 1rem;
`

const TopTag = styled(Card.Tags)`
  z-index: 3;
  top: var(--spacer-3);
  left: var(--spacer-3);
`

export type LearningTrackCardProps = {
  track: Track
  sectionId: string
  productId?: string
  isPreview?: boolean
}

const LearningTrackCard = ({ track, sectionId, productId, isPreview }: LearningTrackCardProps) => {
  const {
    i18n: { language },
    t
  } = useTranslation()
  const { membershipSlug } = useMembershipStore()

  const { data: product = {} as SectionProductDetail, isInitialLoading: isLoadingProductDetails } =
    useGetProductDetails({
      productId: productId || '',
      enabled: Boolean(productId)
    })

  const { authStatus } = useAuthContext()
  const { optimizely } = useContext(OptimizelyContext)
  const handleProductClick = useProductClick({ product, sectionId })
  const { isTestDrawerProduct, methods } = useProduct()
  const internalSalesPageUrl = useSalesPage({ product })

  const { joined, purchased, freeSignUp, accessLink, productHotLink } = product

  const isAuthenticated = authStatus === AuthStatus.AUTHENTICATED
  const trackPlaylistUrl = `/${language}/club/${membershipSlug}/products/${productId}/track/${track.itemId}`
  const trackUrl = accessLink || trackPlaylistUrl
  const redirectLink = getLearningTrackLink({
    isPurchased: purchased,
    isJoined: joined,
    productHotLink,
    trackUrl,
    internalSalesPageUrl
  })

  const callToActions = learningTrackCallToActions({ joined, purchased, freeSignUp, redirectLink })
  const handleEventOptimizelyUserForSale = () => {
    if (freeSignUp && !isAuthenticated) {
      optimizely?.track('click_free_products')
      optimizely?.track('click_login_primary')
    } else if (!freeSignUp && !isAuthenticated) {
      optimizely?.track('click_for_sale_products')
    }
  }

  const handleProductClickValidate = (event: React.MouseEvent<HTMLElement>, productUrl: string) => {
    if (isTestDrawerProduct && !freeSignUp) {
      methods.setProduct({
        itemId: product.id.toString(),
        title: track.title,
        bwImage: { url: track.image?.url },
        category: product.category,
        purchased: Boolean(product.purchased),
        joined: Boolean(product.joined),
        accessLink: product.accessLink,
        productHotLink: product.productHotLink,
        freeSignUp: Boolean(freeSignUp)
      })
    } else {
      handleProductClick(event, productUrl)
    }
    handleEventOptimizelyUserForSale()
  }

  return (
    <Card
      as='div'
      data-test='learning-track-card'
      {...(!isPreview && { onClick: (event) => handleProductClickValidate(event, redirectLink) })}>
      <Card.Body>
        <Card.Thumbnail image={track.image?.url} />
        <TopTag className='_position-absolute'>{!freeSignUp && !purchased && <LockIcon />} </TopTag>

        <Card.HoverInformation>
          <SectionTypeTag
            label={t('home.track')}
            className='_mb-3 _ml-3'
            icon={faFlagSwallowtail}
          />

          <Card.HoverBackground>
            <LineClamp lines={5}>
              <h2 className='_text-3 _text-gray-900 _font-weight-bold'>{track.title}</h2>
            </LineClamp>

            <ButtonsContainer className='_d-flex _align_items-center'>
              {isLoadingProductDetails ? (
                <ContentLoader className='_w-full _h-7 _rounded-lg' />
              ) : (
                callToActions.map((cta) => (
                  <Button
                    key={cta.label}
                    variation={cta.type as Variation}
                    {...(!isPreview && {
                      onClick: (event) => handleProductClickValidate(event, cta.to)
                    })}
                    className='_w-full'>
                    {t(cta.label)}
                  </Button>
                ))
              )}
            </ButtonsContainer>
          </Card.HoverBackground>
        </Card.HoverInformation>
      </Card.Body>
      <Card.Footer>
        <LineClamp lines={2}>
          <h2 className='_text-2 _text-gray-900 _font-weight-bold _mt-2 _mt-md-3 _mb-1'>
            {track.title}
          </h2>
        </LineClamp>
      </Card.Footer>
    </Card>
  )
}

export default LearningTrackCard
